<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import DateTimePickerField from '../../../components/Fields/DateTimePickerField.vue'
import EventCheckpointAutocomplete from '../../../components/Filters/components/EventCheckpointAutocomplete.vue'
import StackLayout from '../../../components/Layouts/StackLayout.vue'
import SelectorField from '../../../components/Selector/SelectorField.vue'
import TableColumn from '../../../components/Table/TableColumn.vue'
import TypographyItem from '../../../components/Typography/TypographyItem.vue'
import { EDetectionActions } from '../../../store/detectionStore/DetectionStoreTypes'
import { ELocalStorage } from '../../../types/enum/LocalStorageEnum'
import {
    EPresenceStatus,
    EPresenceStatusLabels,
    EPresenceStatusOptions
} from '../../../types/enum/StatusEnum'
import type { LocalStorageFilter } from '../../../types/filters/DeviceFilters'
import type { CheckpointModel } from '../../../types/models/CheckpointModel'
import type { DetectionModel } from '../../../types/models/DetectionModel'
import type { EventModel } from '../../../types/models/EventModel'
import { durationCalc, formatTimestamp } from '../../../utils/dateUtils'
import { getItemFromStorage } from '../../../utils/localStorageUtils'
import { isMobile, isMobileEvent } from '../../../utils/viewsUtils'
import DetectionsDesktopFilters from '../DetectionsDesktopFilters.vue'
import DetectionsTableView from '../DetectionsTableView.vue'
import EPCFilter from '../Filters/EPCFilter.vue'

const { t } = useI18n()
const route = useRoute()

const showMobileFilters = ref(isMobile())
isMobileEvent(e => (showMobileFilters.value = e.matches))

const columns = computed(() => ({
    poi_name: { field: 'poi_name', label: t('detection.table.poi_name') },
    epc: { field: 'epc', label: t('detection.table.epc') },
    arrived_at: { field: 'arrived_at', label: t('detection.table.arrived_at') },
    left_at: { field: 'left_at', label: t('detection.table.left_at') },
    duration: { field: 'duration', label: t('detection.table.duration') },
    status: { field: 'status', label: t('detection.table.status') }
}))

const selectedEvent = ref(getItemFromStorage<LocalStorageFilter>(ELocalStorage.FILTERS)?.event)
const selectedCheckpoint = ref(
    getItemFromStorage<LocalStorageFilter>(ELocalStorage.FILTERS)?.checkpoint
)

const selectedArrivedAtFrom = ref<Date | undefined>()
const selectedArrivedAtTo = ref<Date | undefined>()
const selectedLeftAtFrom = ref<Date | undefined>()
const selectedLeftAtTo = ref<Date | undefined>()
const selectedStatus = ref<EPresenceStatus | undefined>(
    (route?.query?.status as EPresenceStatus) ?? EPresenceStatus.ALL
)
const selectedEpc = ref<string | undefined>()

const filters = computed<any>(() => ({
    site_id: selectedEvent.value?.id,
    poi_id: selectedCheckpoint.value?.id,
    arrived_at_from: selectedArrivedAtFrom.value,
    arrived_at_to: selectedArrivedAtTo.value,
    left_at_from: selectedLeftAtFrom.value,
    left_at_to: selectedLeftAtTo.value,
    status: selectedStatus.value ? selectedStatus.value : undefined,
    epc: selectedEpc.value
}))

const handleRowClick = (row: DetectionModel) => {
    selectedEvent.value = { id: row.site_id, name: row.site_name }
    selectedCheckpoint.value = { id: row.poi_id, name: row.poi_name }
}

const exportFilters = ref<{ site?: EventModel; poi?: CheckpointModel }>({})
const exportFileNameData = computed(() => ({
    site_name: exportFilters.value?.site?.name,
    poi_name: exportFilters.value?.poi?.name
}))
const exportFiltersParsed = computed(() => ({
    site_id: exportFilters.value?.site?.id,
    poi_id: exportFilters.value?.poi?.id
}))
</script>

<template>
    <DetectionsTableView
        :storeAction="EDetectionActions.FETCH_PRESENCES"
        :storeExportAction="EDetectionActions.EXPORT_PRESENCES"
        :exportFileNameData="exportFileNameData"
        :disabledExport="!exportFileNameData.site_name"
        :filters="filters"
        :exportFilters="exportFiltersParsed"
        @click="handleRowClick">
        <template #exportFilters>
            <EventCheckpointAutocomplete
                :event="exportFilters.site"
                :checkpoint="exportFilters.poi"
                @selectEvent="exportFilters = { site: $event }"
                @selectCheckpoint="exportFilters = { ...exportFilters, poi: $event }" />
        </template>
        <template #filters>
            <DetectionsDesktopFilters
                :filters="filters"
                :selectedEvent="selectedEvent"
                :selectedCheckpoint="selectedCheckpoint"
                @selectEvent="selectedEvent = $event"
                @selectCheckpoint="selectedCheckpoint = $event">
                <StackLayout direction="row" :gap="8" isResponsiveSmallScreen>
                    <DateTimePickerField
                        clearable
                        :label="$t('detection.filters.arrived_at_from')"
                        :modelValue="filters.arrived_at_from"
                        @change="selectedArrivedAtFrom = $event"
                        class="fit-content" />
                    <DateTimePickerField
                        clearable
                        :label="$t('detection.filters.arrived_at_to')"
                        :modelValue="filters.arrived_at_to"
                        @change="selectedArrivedAtTo = $event"
                        class="fit-content" />
                </StackLayout>
                <StackLayout direction="row" :gap="8" isResponsiveSmallScreen>
                    <DateTimePickerField
                        clearable
                        :label="$t('detection.filters.left_at_from')"
                        :modelValue="filters.left_at_from"
                        @change="selectedLeftAtFrom = $event"
                        class="fit-content" />
                    <DateTimePickerField
                        clearable
                        :label="$t('detection.filters.left_at_to')"
                        :modelValue="filters.left_at_to"
                        @change="selectedLeftAtTo = $event"
                        class="fit-content" />
                </StackLayout>
                <template #rowFilters>
                    <slot name="rowFilters" />
                </template>
            </DetectionsDesktopFilters>
        </template>
        <template #addedFilters></template>
        <template #rowFilters>
            <EPCFilter :epc="filters.epc" @change="selectedEpc = $event" />
            <SelectorField
                label="Status"
                :modelValue="selectedStatus"
                :options="EPresenceStatusOptions"
                @change="selectedStatus = $event as EPresenceStatus" />
        </template>
        <template #columns>
            <TableColumn :field="columns['poi_name'].field" :label="columns['poi_name'].label" />
            <TableColumn :field="columns['epc'].field" :label="columns['epc'].label" />
            <TableColumn
                :field="columns['arrived_at'].field"
                :label="columns['arrived_at'].label"
                v-slot="props">
                <TypographyItem
                    v-if="props.row.arrived_at"
                    :label="formatTimestamp(props.row.arrived_at)" />
            </TableColumn>
            <TableColumn
                :field="columns['left_at'].field"
                :label="columns['left_at'].label"
                v-slot="props">
                <TypographyItem
                    v-if="props.row.left_at"
                    :label="formatTimestamp(props.row.left_at)" />
            </TableColumn>

            <TableColumn
                :field="columns['duration'].field"
                :label="columns['duration'].label"
                v-slot="props">
                <TypographyItem
                    v-if="props.row.left_at && props.row.arrived_at"
                    :label="durationCalc(props.row.left_at, props.row.arrived_at)" />
            </TableColumn>
            <TableColumn
                :field="columns['status'].field"
                :label="columns['status'].label"
                v-slot="props">
                <TypographyItem
                    v-if="props.row.status"
                    :label="$t(EPresenceStatusLabels[props.row.status as EPresenceStatus])" />
            </TableColumn>
        </template>
    </DetectionsTableView>
</template>
